/**
 * ScopeSelector Component
 * Filter bar for the Insights dashboard — styled to match the app's share-to-tenant dropdowns.
 */

import { useMemo, type ReactNode } from 'react';
import { Select, Spin, Switch, Tooltip, theme, Divider } from 'antd';
import { DatePicker } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { FilterOutlined, BulbOutlined } from '@ant-design/icons';
import { useLanguage } from '@/hooks/useLanguage';
import type { TenantNode, InsightFilters, InsightTemplate } from '../types/insights';
import { datePattern } from '@/utils/locale';
import {
  renderAgentOption,
  renderTenantOption,
  AgentPlaceholder,
  TenantPlaceholder,
  buildTenantOptions,
  tenantFilterOption,
  AGENT_SELECT_STYLE,
  TENANT_SELECT_STYLE,
  AGENT_DROPDOWN_STYLE,
  TENANT_DROPDOWN_STYLE,
} from '../utils/selectRenderers';

const { RangePicker } = DatePicker;

const BLUE = '#3b82f6';

interface ScopeSelectorProps {
  tenantTree: TenantNode[];
  agentOptions: { value: string; label: string }[];
  agentOptionsLoading?: boolean;
  filters: InsightFilters;
  onChange: (filters: InsightFilters) => void;
  loading?: boolean;
  templates?: InsightTemplate[];
  topicTemplateId?: string;
  onTopicTemplateChange?: (templateId: string | undefined) => void;
  /** When ON, render a small "Refreshing…" indicator at the end of the
   *  filter row. Used by the Cost Breakdown tab during background revalidation. */
  isRefreshing?: boolean;
  /** A tab's own buttons in this row (item 86): Back at its start, New template at its end. */
  start?: ReactNode;
  end?: ReactNode;
}

// Presets are computed FRESH every render (via the function below) — not
// captured at module load. The old top-level-const version froze "today"
// to whenever the JS bundle first loaded, so clicking "Last 7 days" hours
// later (or after a tab idled overnight) silently used a stale end date.
function buildRangePresets(t: (k: string) => string): { label: string; value: [Dayjs, Dayjs] }[] {
  const now = dayjs();
  return [
    { label: t('common.today'),        value: [now,                    now] },
    { label: t('common.yesterday'),    value: [now.subtract(1, 'day'), now.subtract(1, 'day')] },
    { label: t('common.last7Days'),  value: [now.subtract(6, 'day'),  now] },
    { label: t('common.last30Days'), value: [now.subtract(29, 'day'), now] },
    { label: t('common.last90Days'), value: [now.subtract(89, 'day'), now] },
  ];
}

export function ScopeSelector({
  tenantTree,
  agentOptions,
  agentOptionsLoading = false,
  filters,
  onChange,
  loading = false,
  templates = [],
  topicTemplateId,
  onTopicTemplateChange,
  isRefreshing = false,
  start,
  end,
}: ScopeSelectorProps) {
  const { t } = useLanguage();
  const { token } = theme.useToken();

  const handleTenantChange = (values: string[]) => onChange({ ...filters, tenantIds: values });
  const handleAgentChange = (values: string[]) => onChange({ ...filters, chatbotIds: values });
  const handleRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      onChange({
        ...filters,
        dateFrom: dates[0].format('YYYY-MM-DD'),
        dateTo: dates[1].format('YYYY-MM-DD'),
      });
    }
  };

  // The same value while the dates are the same: a fresh array on every render
  // made the open calendar jump back to the selected month whenever the page
  // re-rendered (a run's progress poll), so earlier months were unreachable.
  const rangeValue = useMemo<[Dayjs, Dayjs]>(
    () => [dayjs(filters.dateFrom), dayjs(filters.dateTo)], [filters.dateFrom, filters.dateTo]);

  const tenantSelectOptions = buildTenantOptions(tenantTree);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 10px',
        background: token.colorBgContainer,
        borderRadius: token.borderRadiusLG,
        border: `1px solid ${token.colorBorderSecondary}`,
        // Wraps on a narrow screen: a sideways-scrolling row hid the date range on a phone.
        flexWrap: 'wrap',
        rowGap: 8,
      }}
    >
      {start && <span className="ins-scope__start">{start}</span>}
      <FilterOutlined style={{ fontSize: 13, color: BLUE, flexShrink: 0 }} />

      <Divider type="vertical" style={{ height: 20, margin: '0 2px', borderColor: token.colorBorderSecondary }} />

      {/* Tenant selector */}
      <Select
        mode="multiple"
        placeholder={<TenantPlaceholder text={t('insights.scopeSelector.tenantsPlaceholder')} />}
        value={filters.tenantIds}
        onChange={handleTenantChange}
        loading={loading}
        allowClear
        maxTagCount={2}
        style={{ ...TENANT_SELECT_STYLE, flex: '1 1 140px', minWidth: 120 }}
        showSearch
        filterOption={tenantFilterOption}
        size="small"
        popupMatchSelectWidth={false}
        dropdownStyle={TENANT_DROPDOWN_STYLE}
        options={tenantSelectOptions}
        optionRender={(option) => renderTenantOption(option, tenantTree, token.colorTextTertiary)}
      />

      {/* Include sub-tenants — inline, right next to the tenant selector.
          Only visible when a tenant is picked, because the flag is meaningless
          without one. Lives in the shared filter shape so every tab can opt
          into it (today only Cost Breakdown reads it). */}
      {filters.tenantIds.length > 0 && (
        <Tooltip title={t('insights.expandTenantFilterTip')}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              color: token.colorTextSecondary,
              cursor: 'help',
              flexShrink: 0,
            }}
          >
            <span>{t('insights.pulse.subTenants')}</span>
            <Switch
              size="small"
              checked={filters.includeDescendants ?? false}
              onChange={(v) => onChange({ ...filters, includeDescendants: v })}
            />
          </span>
        </Tooltip>
      )}

      {/* Agent selector */}
      <Select
        mode="multiple"
        placeholder={<AgentPlaceholder text={t('insights.scopeSelector.agentsPlaceholder')} />}
        value={filters.chatbotIds}
        onChange={handleAgentChange}
        loading={agentOptionsLoading}
        allowClear
        maxTagCount={2}
        style={{ ...AGENT_SELECT_STYLE, flex: '1 1 140px', minWidth: 120 }}
        options={agentOptions}
        optionFilterProp="label"
        showSearch
        size="small"
        popupMatchSelectWidth={false}
        dropdownStyle={AGENT_DROPDOWN_STYLE}
        optionRender={renderAgentOption}
        notFoundContent={
          <span style={{ color: token.colorTextDisabled, fontSize: 12 }}>
            {t('insights.scopeSelector.noAgentsYet')}
          </span>
        }
      />

      <Divider type="vertical" style={{ height: 20, margin: '0 2px', borderColor: token.colorBorderSecondary }} />

      {/* Date range picker */}
      <RangePicker
        size="small"
        value={rangeValue}
        onChange={handleRangeChange}
        presets={buildRangePresets(t)}
        allowClear={false}
        format={datePattern('monthDayYear')}
        style={{ flex: '1 1 220px', minWidth: 200, maxWidth: 320 }}
      />

      {/* Topic template selector */}
      {onTopicTemplateChange && templates.length > 0 && (
        <>
          <Divider type="vertical" style={{ height: 20, margin: '0 2px', borderColor: token.colorBorderSecondary }} />
          <Select
            size="small"
            placeholder={
              <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
                <BulbOutlined style={{ fontSize: 11 }} />
                {t('insights.scopeSelector.defaultTopicTemplate')}
              </span>
            }
            value={topicTemplateId || '__system__'}
            onChange={(val) => onTopicTemplateChange(val === '__system__' ? undefined : val)}
            style={{ flex: '0 1 180px', minWidth: 130 }}
            popupMatchSelectWidth={false}
            dropdownStyle={{ minWidth: 240 }}
            options={[
              { value: '__system__', label: t('insights.scopeSelector.defaultTopicTemplate') },
              ...templates
                .filter(tmpl => (tmpl.response_type === 'multi_label' || tmpl.response_type === 'label') && !tmpl.is_system)
                .map(tmpl => ({ value: tmpl.template_id, label: tmpl.template_name })),
            ]}
          />
        </>
      )}

      {/* Background-refresh indicator — sits at the END of the filter row
          (margin-inline-start: auto) so it pins to the right without
          forcing a new line. Only renders during a Cost-Breakdown refetch. */}
      {isRefreshing && (
        <span
          style={{
            marginInlineStart: 'auto',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
            color: token.colorTextSecondary,
            flexShrink: 0,
          }}
        >
          <Spin size="small" />
          <span>Refreshing…</span>
        </span>
      )}
      {end && <span className="ins-scope__end">{end}</span>}
    </div>
  );
}
